/**
 * The customer's own live promotions (M5 / D9).
 *
 * ── Why this had to exist ───────────────────────────────────────────────
 *
 * Promo grants were invisible to the customer holding them. The bell fires
 * `promo_granted` and `promo_expiring`; the redemption page hands the redeemer
 * to this very page with a comment promising it lands "where the grant now
 * shows — the confirmation is the state, not a toast." Nothing showed it. A
 * grant surfaced only as an "Also funded this period" line AFTER it had been
 * spent, so the flow deliberately declined to give a toast and then gave
 * nothing at all.
 *
 * ── Renders nothing when there is nothing ───────────────────────────────
 *
 * Most tenants have no promotion, and a permanently empty "Promotions" card
 * would ask every one of them "what promotion?" — the same reasoning the
 * funding tiles already use for their promo and credits entries.
 */
import { useEffect, useState } from 'react'
import { Gift } from 'lucide-react'
import { useTranslation } from '@papan/i18n'
import { request } from '@papan/api-client'
import type { PromoEffect } from '@papan/types'
import { formatBillingMicro, formatCurrencyDateLocal } from './money.js'
import { CARD, SECTION_LABEL } from './styles.js'

interface ActiveGrant {
  id: string
  effects: PromoEffect[]
  endsAt: string
  creditRemainingBillingMicro?: number
  /** The unit the amount above is in. Absent when the server cannot name one
   *  — see `creditCurrency` in the route. */
  creditCurrency?: string
}

/** One line per grant, describing what it carries.
 *
 *  Unknown effect kinds degrade to "Promotional access" rather than throwing —
 *  the same defence-in-depth the entitlement resolver and the notification
 *  registry already take, so a grant written by a newer server does not break
 *  an older tab. */
function describe(g: ActiveGrant, t: (k: string, o?: Record<string, unknown>) => string): string {
  // BOTH halves required. The amount is billing-currency micro, so rendering it
  // without the currency the server named is how this line came to print an
  // RM30 grant as "$30.00" beside a correctly-labelled "MYR 0.00" balance on
  // the same card. A grant whose currency the server cannot state degrades to
  // the generic line — the same grant the draw-down predicate already refuses
  // to spend, so there is no balance worth advertising.
  if (
    typeof g.creditRemainingBillingMicro === 'number'
    && g.creditRemainingBillingMicro > 0
    && typeof g.creditCurrency === 'string'
  ) {
    return t('activePromo.creditLeft', {
      amount: formatBillingMicro(g.creditRemainingBillingMicro, g.creditCurrency),
    })
  }
  return t('activePromo.generic')
}

export function ActivePromoSection({ currency }: {
  /** The page's billing currency — the grant end date renders in its locale.
   *  Not `g.creditCurrency`, which is optional (absent for a non-credit grant)
   *  and would fall back to US order on a MYR deployment. */
  currency: string
}) {
  const { t } = useTranslation('billing')
  const [grants, setGrants] = useState<ActiveGrant[]>([])

  useEffect(() => {
    let alive = true
    request<{ grants?: ActiveGrant[] }>('/api/billing/promo/active')
      .then((r) => { if (alive) setGrants(r.grants ?? []) })
      // Silent: this is additive information on a page that works without it,
      // and an error card would be louder than the feature.
      .catch(() => { if (alive) setGrants([]) })
    return () => { alive = false }
  }, [])

  if (grants.length === 0) return null

  return (
    <section className="space-y-3">
      <h3 className={`${SECTION_LABEL} flex items-center gap-1.5`}>
        <Gift size={12} />
        {t('activePromo.heading')}
      </h3>
      <div className={`${CARD} p-4 space-y-2`}>
        {grants.map((g) => (
          <div key={g.id} className="flex items-baseline justify-between gap-3 text-sm">
            <span className="themed-text">{describe(g, t)}</span>
            {/* An ABSOLUTE date, like the notification copy: this card is
                re-read weeks later and a relative "3 days left" would age into
                a lie. */}
            <span className="themed-text-muted text-xs shrink-0">
              {t('activePromo.endsAt', { date: formatCurrencyDateLocal(g.endsAt, currency) })}
            </span>
          </div>
        ))}
      </div>
    </section>
  )
}
